import type { MachineAccessGrant } from "@/transport/fleet"

type Waiter = {
  resolve: (grant: MachineAccessGrant) => void
  reject: (error: unknown) => void
  signal: AbortSignal
}

/**
 * Turns machine access requests made in the same tick into one batch request.
 * Each batch also renews every machine `companions` names (those whose grants
 * are missing or about to expire), and `adopt` receives their grants, so a
 * browser opening a whole fleet asks in one batch instead of once per machine.
 */
export function createMachineAccessBatcher(input: {
  issueMany: (names: readonly string[], signal: AbortSignal) => Promise<Map<string, MachineAccessGrant | Error>>
  companions: () => readonly string[]
  adopt: (name: string, grant: MachineAccessGrant) => void
}) {
  let queued: Map<string, Waiter[]> | undefined

  const flush = async (batch: Map<string, Waiter[]>) => {
    const waiters = [...batch.values()].flat()
    const controller = new AbortController()
    let open = waiters.length
    const detach: Array<() => void> = []
    for (const waiter of waiters) {
      const abandon = () => {
        waiter.reject(waiter.signal.reason)
        // The request only stops once nobody is waiting for it.
        if (--open === 0) controller.abort()
      }
      if (waiter.signal.aborted) abandon()
      else {
        waiter.signal.addEventListener("abort", abandon, { once: true })
        detach.push(() => waiter.signal.removeEventListener("abort", abandon))
      }
    }
    if (controller.signal.aborted) return
    const names = [...new Set([...batch.keys(), ...input.companions()])]
    try {
      const results = await input.issueMany(names, controller.signal)
      for (const [name, result] of results) {
        const requested = batch.get(name)
        if (!requested) {
          // Nobody waits on a companion, so only its grant matters.
          if (!(result instanceof Error)) input.adopt(name, result)
          continue
        }
        for (const waiter of requested) result instanceof Error ? waiter.reject(result) : waiter.resolve(result)
      }
      const missing = new Error("Server returned no machine access for the machine")
      for (const [name, requested] of batch) {
        if (!results.has(name)) for (const waiter of requested) waiter.reject(missing)
      }
    } catch (error) {
      for (const waiter of waiters) waiter.reject(error)
    } finally {
      for (const remove of detach) remove()
    }
  }

  return {
    request(name: string, signal: AbortSignal): Promise<MachineAccessGrant> {
      return new Promise((resolve, reject) => {
        if (!queued) {
          const batch = (queued = new Map())
          queueMicrotask(() => {
            queued = undefined
            void flush(batch)
          })
        }
        const waiters = queued.get(name) ?? []
        waiters.push({ resolve, reject, signal })
        queued.set(name, waiters)
      })
    },
  }
}
